/**
 * HeadlessPlaylistSection - Client Component
 *
 * Routes section rendering to either UnifiedMediaSection or Disclaimer
 * based on component type and detected/explicit mode.
 */

"use client";

import type { ReactElement } from "react";
import UnifiedMediaSection from "../UnifiedMediaSection/UnifiedMediaSection";
import type { MediaData } from "../../types/livelanding.types";
import type {
  MediaData as CategoryMediaData,
  TabItem as CategoryTabItem,
} from "../../utils/playlistCategoryUtils";
import {
  toBoolean,
  getDefaultHeadlessSectionId,
  resolveHeadlessPlaylistMode,
  type HeadlessBoolean,
} from "./headlessPlaylistUtils";

export type {
  HeadlessPlaylistType,
  HeadlessPlaylistLayout,
  HeadlessBoolean,
  HeadlessPlaylistRenderMode,
} from "./headlessPlaylistUtils";
export { getDefaultHeadlessSectionId, resolveHeadlessPlaylistMode } from "./headlessPlaylistUtils";

export interface HeadlessPlaylistSectionProps {
  readonly type: "horizontal" | "vertical" | "tabs" | "disclaimer";
  readonly title: string;
  readonly titleCt?: string;
  readonly playlistID?: string;
  readonly sectionId?: string;
  readonly sectionMode?: "live" | "previous" | "upcoming" | "playlist" | "category";
  readonly layoutTypeMobile?: "scroll" | "list" | "grid" | "tabs";
  readonly layoutTypeDesktop?: "scroll" | "list" | "grid" | "tabs";
  readonly isLiveNudge?: HeadlessBoolean;
  readonly isTimestamp?: HeadlessBoolean;
  readonly isViewCount?: HeadlessBoolean;
  readonly isTrailerPlay?: HeadlessBoolean;
  readonly autoplay?: HeadlessBoolean;
  readonly branding?: HeadlessBoolean;
  readonly dock?: HeadlessBoolean;
  readonly enableShowMore?: HeadlessBoolean;
  readonly increment?: number;
  readonly remindMeText?: string;
  readonly reminderSetText?: string;
  readonly language?: string;
  readonly apiBaseUrl?: string;
  readonly data?: MediaData | null;
  readonly tabs?: Array<Record<string, CategoryTabItem>>;
  readonly initialMediaData?: CategoryMediaData | null;
  readonly initialMediaDataByTab?: Record<string, CategoryMediaData | null>;
  readonly initialPaginationByTab?: Record<
    string,
    { page: number; limit: number; total: number } | null
  >;
  readonly tabsApiBaseUrl?: string;
  readonly influencerId?: string;
  readonly reminderService?: import("../UpcomingLive/UpcomingLive").ReminderService;
  readonly reduceMobileSectionHeadingSize?: boolean;
}

export default function HeadlessPlaylistSection({
  type,
  title,
  titleCt,
  playlistID,
  sectionId,
  sectionMode,
  layoutTypeMobile,
  isLiveNudge,
  isTimestamp,
  enableShowMore,
  increment,
  remindMeText,
  reminderSetText,
  language,
  apiBaseUrl,
  data,
  tabs,
  initialMediaData,
  initialMediaDataByTab,
  initialPaginationByTab,
  tabsApiBaseUrl,
  influencerId,
  reminderService,
  reduceMobileSectionHeadingSize,
}: Readonly<HeadlessPlaylistSectionProps>): ReactElement {
  const mode =
    sectionMode ??
    resolveHeadlessPlaylistMode({
      type,
      title,
      sectionId,
      sectionMode,
      remindMeText,
      isLiveNudge,
      isTimestamp,
      layoutTypeMobile,
    });
  const effectiveSectionId = sectionId ?? getDefaultHeadlessSectionId(title);

  if (mode === "category") {
    return (
      <UnifiedMediaSection
        mode="category"
        title={title}
        titleCt={titleCt}
        sectionId={effectiveSectionId}
        language={language}
        playListId={playlistID}
        tabs={tabs}
        initialMediaData={initialMediaData}
        initialMediaDataByTab={initialMediaDataByTab}
        initialPaginationByTab={initialPaginationByTab}
        tabsApiBaseUrl={tabsApiBaseUrl}
        reduceMobileSectionHeadingSize={reduceMobileSectionHeadingSize}
      />
    );
  }

  return (
    <UnifiedMediaSection
      mode={mode}
      title={title}
      titleCt={titleCt}
      data={data}
      sectionId={effectiveSectionId}
      setReminderText={remindMeText}
      reminderSetText={reminderSetText}
      enableMobileList={layoutTypeMobile === "scroll" || layoutTypeMobile === "list"}
      enableShowMore={toBoolean(enableShowMore)}
      increment={increment}
      playListId={playlistID}
      apiBaseUrl={apiBaseUrl}
      influencerId={influencerId}
      reminderService={reminderService}
      reduceMobileSectionHeadingSize={reduceMobileSectionHeadingSize}
    />
  );
}
